import { paths } from '../routes';

/**
 * The customer-mail payment guard, as the admin UI explains it (PO,
 * 2026-10-30; ADR-0016 Amendment 9): a mail that would ask for money it
 * cannot be paid with is not sent. The booking can always be saved without a
 * mail.
 *
 * NOT a copy of the rule. The rule is `customerMailBlock` in `@kolula/shared`,
 * applied by both backends' mail senders (ADR-0020: the endpoint decides). The
 * screen reads the answer the price preview returns for the booking on screen
 * (`customerMail`), and the refusal a save returns (`mailBlocked`), and only
 * says what to do about it.
 */

export type CustomerMailBlockReason =
    | 'bank_details_missing'
    | 'stripe_not_connected'
    | 'payment_settings_missing';

/** What a backend returns when it refused the mail. */
export interface CustomerMailRefusal {
    reason: CustomerMailBlockReason;
    code: string;
    message: string;
}

/** The price preview's answer for the booking on screen. */
export type CustomerMailPreview =
    | { status: 'allowed' }
    | { status: 'blocked', refusal: CustomerMailRefusal }
    // Could not be established: the screen does not guess, the endpoint decides.
    | { status: 'unknown' };

/** The refusal the preview reports, or `null` when the mail may go out or the answer is unknown. */
export const customerMailRefusalOf = (
    preview: CustomerMailPreview | null | undefined,
): CustomerMailRefusal | null =>
    (preview?.status === 'blocked' ? preview.refusal : null);

/** Where each reason is fixed, and what the hint says. */
export const CUSTOMER_MAIL_FIX: Record<CustomerMailBlockReason, { to: string, labelKey: string }> = {
    bank_details_missing: {
        to: `${paths.account}#bank-details`,
        labelKey: 'customer_mail_guard.reason.bank_details_missing',
    },
    stripe_not_connected: {
        to: `${paths.account}#payment-provider`,
        labelKey: 'customer_mail_guard.reason.stripe_not_connected',
    },
    payment_settings_missing: {
        to: `${paths.rentalProducts}#service-configuration`,
        labelKey: 'customer_mail_guard.reason.payment_settings_missing',
    },
};

/**
 * Reads `mailBlocked` off a save response, whatever its shape. A refusal the
 * screen did not foresee (an API race, a price it could not know) still has
 * to be shown — the save succeeded and the customer heard nothing.
 */
export const mailBlockedOf = (response: unknown): CustomerMailRefusal | null => {
    const parsed = typeof response === 'string' ? safeParse(response) : response;
    const blocked = (parsed as { mailBlocked?: unknown } | null | undefined)?.mailBlocked;
    if (!blocked || typeof blocked !== 'object') return null;
    const reason = (blocked as { reason?: unknown }).reason;
    return reason === 'bank_details_missing' || reason === 'stripe_not_connected' || reason === 'payment_settings_missing'
        ? (blocked as CustomerMailRefusal)
        : null;
};

const safeParse = (text: string): unknown => {
    try {
        return JSON.parse(text);
    } catch {
        return null;
    }
};

/**
 * The toast after a save that may have asked for a mail: the screen's own
 * success, or — when the payment rule refused the mail — a warning that says
 * the booking is saved and why the customer was not mailed. Never a plain
 * success for a refused mail.
 */
export const savedOutcomeToast = (
    t: (key: string, values?: Record<string, string>) => string,
    mailBlocked: CustomerMailRefusal | null | undefined,
    successMessage: string,
): { message: string, severity: 'success' | 'warning' } => {
    if (!mailBlocked) return { message: successMessage, severity: 'success' };
    return {
        message: t('customer_mail_guard.saved_not_sent', { reason: t(CUSTOMER_MAIL_FIX[mailBlocked.reason].labelKey) }),
        severity: 'warning',
    };
};
